/**
 * Planner icon set — 24px grid, 1.8 stroke, currentColor. One family so every
 * button reads the same weight. Decorative by default (aria-hidden); the
 * button text carries the name.
 */
import React from 'react';

type IconProps = { className?: string };

const Stroke = ({ className = 'h-4 w-4', children }: IconProps & { children: React.ReactNode }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const IconPin = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 21s-6.5-6.2-6.5-11.2a6.5 6.5 0 0113 0C18.5 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.8" r="2.3" />
  </Stroke>
);

export const IconPinPlus = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 21s-6.5-6.2-6.5-11.2a6.5 6.5 0 0113 0C18.5 14.8 12 21 12 21z" />
    <path d="M12 7.3v5M9.5 9.8h5" />
  </Stroke>
);

export const IconRoute = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="6" cy="19" r="2" />
    <circle cx="18" cy="5" r="2" />
    <path d="M8 19h7.5a3.5 3.5 0 000-7h-7a3.5 3.5 0 010-7H16" />
  </Stroke>
);

export const IconMoon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z" />
  </Stroke>
);

export const IconTent = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M3 20h18M12 4L4 20M12 4l8 16M12 4v0M9.5 20L12 14l2.5 6" />
  </Stroke>
);

export const IconCompass = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" />
  </Stroke>
);

export const IconPlus = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 5v14M5 12h14" />
  </Stroke>
);

export const IconDownload = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 4v11M7.5 10.5L12 15l4.5-4.5M5 19h14" />
  </Stroke>
);

export const IconPrinter = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M7 9V4h10v5M7 17H5a1.5 1.5 0 01-1.5-1.5v-5A1.5 1.5 0 015 9h14a1.5 1.5 0 011.5 1.5v5A1.5 1.5 0 0119 17h-2" />
    <path d="M7 14h10v6H7z" />
  </Stroke>
);

export const IconRestart = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M4 12a8 8 0 108-8 8.3 8.3 0 00-5.7 2.3L4 8.5" />
    <path d="M4 4v4.5h4.5" />
  </Stroke>
);

export const IconChevronDown = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M6 9l6 6 6-6" />
  </Stroke>
);

export const IconChevronRight = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M9 6l6 6-6 6" />
  </Stroke>
);

export const IconArrowLeft = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M19 12H5M11 6l-6 6 6 6" />
  </Stroke>
);

export const IconSignIn = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M14 4h4a2 2 0 012 2v12a2 2 0 01-2 2h-4M10 16l4-4-4-4M14 12H4" />
  </Stroke>
);

export const IconSearch = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M20 20l-4.2-4.2" />
  </Stroke>
);

export const IconFolder = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M3.5 7.5A1.5 1.5 0 015 6h4l2 2h8a1.5 1.5 0 011.5 1.5v8A1.5 1.5 0 0119 19H5a1.5 1.5 0 01-1.5-1.5z" />
  </Stroke>
);

export const IconCar = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M5 16.5V12l1.8-4.6A1.5 1.5 0 018.2 6.5h7.6a1.5 1.5 0 011.4.9L19 12v4.5" />
    <path d="M4 12h16v4.5H4z" />
    <path d="M6.5 16.5V18M17.5 16.5V18" />
  </Stroke>
);

export const IconFlag = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M6 21V4M6 4.5h10.5l-1.8 4 1.8 4H6" />
  </Stroke>
);

export const IconMore = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="5.5" cy="12" r="1.2" fill="currentColor" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    <circle cx="18.5" cy="12" r="1.2" fill="currentColor" />
  </Stroke>
);

export const IconMoreVertical = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="5.5" r="1.2" fill="currentColor" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    <circle cx="12" cy="18.5" r="1.2" fill="currentColor" />
  </Stroke>
);

export const IconGrip = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="9" cy="6" r="1.1" fill="currentColor" />
    <circle cx="15" cy="6" r="1.1" fill="currentColor" />
    <circle cx="9" cy="12" r="1.1" fill="currentColor" />
    <circle cx="15" cy="12" r="1.1" fill="currentColor" />
    <circle cx="9" cy="18" r="1.1" fill="currentColor" />
    <circle cx="15" cy="18" r="1.1" fill="currentColor" />
  </Stroke>
);

export const IconX = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
  </Stroke>
);

export const IconMinus = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M5 12h14" />
  </Stroke>
);

export const IconArrowUp = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </Stroke>
);

export const IconArrowDown = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 5v14M6 13l6 6 6-6" />
  </Stroke>
);

export const IconCalendar = (p: IconProps) => (
  <Stroke {...p}>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </Stroke>
);

export const IconCheck = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </Stroke>
);

export const IconShare = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 15V4M7.5 8.5L12 4l4.5 4.5" />
    <path d="M5 13v5.5A1.5 1.5 0 006.5 20h11a1.5 1.5 0 001.5-1.5V13" />
  </Stroke>
);

export const IconNote = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M5 4.5h10l4 4V19a1.5 1.5 0 01-1.5 1.5h-12A1.5 1.5 0 014 19V6a1.5 1.5 0 011-1.5z" />
    <path d="M8 11h8M8 15h5" />
  </Stroke>
);

export const IconPencil = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M4 20h4L19 9a2.1 2.1 0 00-3-3L5 17z" />
  </Stroke>
);

export const IconSparkle = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 3.5l1.8 4.7 4.7 1.8-4.7 1.8L12 16.5l-1.8-4.7L5.5 10l4.7-1.8z" />
    <path d="M18.5 15.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
  </Stroke>
);

export const IconWarn = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 4l9 15.5H3z" />
    <path d="M12 10v4M12 17v.01" />
  </Stroke>
);

export const IconMap = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M9 5L3.5 7v12L9 17l6 2 5.5-2V5L15 7z" />
    <path d="M9 5v12M15 7v12" />
  </Stroke>
);

export const IconList = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M9 6.5h11M9 12h11M9 17.5h11" />
    <circle cx="4.5" cy="6.5" r="1" fill="currentColor" />
    <circle cx="4.5" cy="12" r="1" fill="currentColor" />
    <circle cx="4.5" cy="17.5" r="1" fill="currentColor" />
  </Stroke>
);

export const IconSplit = (p: IconProps) => (
  <Stroke {...p}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="M13.5 4.5v15M16 9h2M16 12h2M16 15h2" />
  </Stroke>
);

export const IconChat = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M5 18.5V7a2 2 0 012-2h10a2 2 0 012 2v7.5a2 2 0 01-2 2H8.5z" />
    <path d="M9 10.5h.01M12 10.5h.01M15 10.5h.01" />
  </Stroke>
);

export const IconCaravan = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M3 16.5V8a2 2 0 012-2h10.5a4.5 4.5 0 014.5 4.5v6H3z" />
    <path d="M6.5 10h4v3h-4zM20 16.5h2" />
    <circle cx="9" cy="17.5" r="1.8" />
  </Stroke>
);

export const IconMotorhome = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M3 17V6.5A1.5 1.5 0 014.5 5h10l2 4H20a1 1 0 011 1v7H3z" />
    <path d="M6 8.5h5v3H6zM14.5 9h2" />
    <circle cx="7" cy="17.5" r="1.6" />
    <circle cx="17" cy="17.5" r="1.6" />
  </Stroke>
);

export const IconOffroad = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M3.5 15.5V11l2-4.5h9L17 11h3.5v4.5" />
    <path d="M3.5 11h17M10 6.5V11" />
    <circle cx="7.5" cy="16.5" r="2" />
    <circle cx="16.5" cy="16.5" r="2" />
  </Stroke>
);

export const IconLocate = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="12" r="3.5" />
    <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" />
  </Stroke>
);

export const IconClock = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </Stroke>
);

export const IconInfo = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5M12 8h.01" />
  </Stroke>
);

/** Placeholder for a stop with no photo: hills under a sun. */
export const IconLandscape = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M3 18.5l5.5-7 4 5 2.5-3 6 5z" />
    <circle cx="16.5" cy="7.5" r="1.8" />
  </Stroke>
);

export const IconCopy = (p: IconProps) => (
  <Stroke {...p}>
    <rect x="8.5" y="8.5" width="11.5" height="11.5" rx="2" />
    <path d="M15.5 8.5V6A2 2 0 0013.5 4H6a2 2 0 00-2 2v7.5a2 2 0 002 2h2.5" />
  </Stroke>
);

export const IconLink = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M10 14a4 4 0 005.66 0l3-3a4 4 0 00-5.66-5.66l-1 1" />
    <path d="M14 10a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66l1-1" />
  </Stroke>
);

export const IconTrash = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l.8 12.2A1.5 1.5 0 008.8 20.5h6.4a1.5 1.5 0 001.5-1.3L17.5 7" />
    <path d="M10.5 11v6M13.5 11v6" />
  </Stroke>
);

export const IconFuel = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M5 20V5.5A1.5 1.5 0 016.5 4h6A1.5 1.5 0 0114 5.5V20M4 20h11M5 10h9" />
    <path d="M14 8.5l2.5 0 2 2v6.5a1.5 1.5 0 01-3 0V13.5H14" />
  </Stroke>
);

export const IconDrop = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 3.5s-6 6.6-6 10.8a6 6 0 0012 0C18 10.1 12 3.5 12 3.5z" />
  </Stroke>
);

export const IconLock = (p: IconProps) => (
  <Stroke {...p}>
    <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
    <path d="M8 10.5V8a4 4 0 018 0v2.5" />
  </Stroke>
);

export const IconChevronUp = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M6 15l6-6 6 6" />
  </Stroke>
);

export const IconMail = (p: IconProps) => (
  <Stroke {...p}>
    <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
    <path d="M4 7l8 6 8-6" />
  </Stroke>
);

/** A speech bubble: text messages. */
export const IconMessage = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M20 11.5c0 4-3.6 7-8 7a9 9 0 01-3-.5L4.5 19.5l1.2-3.3A6.6 6.6 0 014 11.5c0-4 3.6-7 8-7s8 3 8 7z" />
  </Stroke>
);

/** A round bubble with a handset: WhatsApp, drawn in the planner's own line weight. */
export const IconWhatsApp = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M4.5 19.5l1.1-3.6A8 8 0 1112 20a8 8 0 01-3.9-1z" />
    <path d="M9.2 8.6c.2-.4.5-.4.8-.4l.5.1.8 1.8-.6.8c.4.9 1.2 1.7 2.2 2.2l.8-.6 1.8.8v.5c0 .4-.4 1-1.2 1.1-1.6.2-5.3-2.7-5.3-5.1 0-.5.1-.9.2-1.2z" />
  </Stroke>
);

export const IconSettings = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </Stroke>
);

/** A blank page with a plus: start something new (not "reset"). */
export const IconFilePlus = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M14 3.5H7.5a2 2 0 00-2 2v13a2 2 0 002 2h9a2 2 0 002-2V8z" />
    <path d="M14 3.5V8h4.5" />
    <path d="M12 11.5v5M9.5 14h5" />
  </Stroke>
);

export const IconFacebook = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M13.2 20.4V13.5h2.3l.4-2.6h-2.7V9.3c0-.8.3-1.3 1.4-1.3h1.4V5.8a17 17 0 00-2-.1c-2 0-3.3 1.2-3.3 3.4v1.8H8.5v2.6h2.2v6.9" />
  </Stroke>
);

/** Trip settings (dates, vehicle): two sliders. */
export const IconSliders = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
    <circle cx="15" cy="7" r="2.2" />
    <circle cx="9" cy="17" r="2.2" />
  </Stroke>
);

export const IconPhone = (p: IconProps) => (
  <Stroke {...p}>
    <rect x="7" y="2.5" width="10" height="19" rx="2.2" />
    <path d="M11 18.5h2" />
  </Stroke>
);
